/**
 * needAiColumn —— 「是否需包含AI」列（自包含：常量 + 值域转换 + 单元格 + 列构建）。
 *
 * 【为何是 custom 列而不是 doc 列】两条推导，缺一不可（两条都踩过）：
 *  ① doc 列的渲染是 `value={(value as string) || undefined}` —— 会把 boolean `false`
 *     （用户选「否」）**吞成空值**、显示占位符，看起来像没填；
 *  ② doc 列的批量弹层用内置 Select + `!value` 判空 —— 选「否」时「确定」按钮**被禁用**。
 * custom 列可自行做值域转换、必填红框与表头星号（batchEdit.required）。
 *
 * ⚠️ 这两条推导必须与实现同处：只看到 `kind:'custom'` 的后来者会以为这是随意选择，
 * 然后「顺手统一成 doc 列」，把上面两个 bug 一起带回来。
 *
 * 【与折叠面板逐项对齐】`CooperationFormList/index.tsx:531-551`：
 *  - 标题：面板 label 为「是否需包含AI」（原表格写作「需含AI」）
 *  - 控件：面板是 radio 是/否；表格列窄，用 Select 承载同一「是/否」二选一
 *  - 值域：面板 options 的 value 是 **boolean**（`true`/`false`），后端契约
 *    `need_ai?: boolean`（api.ts:2005/12222/17972/29070）。原表格实现写成
 *    `'YES'`/`'NO'` 字符串 → 数据模型与面板不一致，提交时也无法反序列化为 Boolean。
 *  - 必填：面板 `required: true`
 * ⚠️ 控件值用 `'TRUE'`/`'FALSE'` 字符串中转，仅因 Select 对 falsy 值（`false`）
 *    会回退显示占位符、引擎批量弹层的「确定」按钮也以 `!value` 判空；
 *    **写回 row 的始终是 boolean**，数据模型不受影响。
 */
import React, { useEffect, useState } from 'react';
import { Form, Select, Tooltip } from '@bedrock/components';
import { CELL_SELECT_DROPDOWN_PROPS, INPUT_VALIDATE_DEBOUNCE_MS, type ColumnMeta } from '@mpc/common/src/components/ProTableListForm';
import { isEmptyFieldValue } from './propertyValidation';

const NEED_AI_TITLE = '是否需包含AI';
const NEED_AI_COLUMN_WIDTH = 130;
const NEED_AI_OPTIONS = [
  { name: '是', value: 'TRUE' },
  { name: '否', value: 'FALSE' },
];
/** row.need_ai(boolean) → Select 值（字符串中转） */
const needAiToSelectValue = (v: unknown): string | undefined =>
  v === true ? 'TRUE' : v === false ? 'FALSE' : undefined;
/** Select 值 → row.need_ai(boolean)；清空时写 undefined */
const needAiFromSelectValue = (v: unknown): boolean | undefined =>
  v === 'TRUE' ? true : v === 'FALSE' ? false : undefined;

/** 需含AI 单元格：必填红框判定与 PropField 同一套（外部全量校验 / 本格编辑后防抖提示） */
const NeedAiCell = React.memo(function NeedAiCell({ row, onUpdate, forceValidate }: {
  row: Record<string, any>;
  onUpdate: (id: number, patch: Record<string, any>) => void;
  /** 外部全量校验态：true 时所有必填未填格标红 */
  forceValidate?: boolean;
}) {
  const invalid = isEmptyFieldValue(row.need_ai);
  const [touched, setTouched] = useState(false);
  // 输入期提示防抖（时长与引擎 INPUT_VALIDATE_DEBOUNCE_MS 一致），避免值更新前闪现红框
  const [debouncedInvalid, setDebouncedInvalid] = useState(false);
  useEffect(() => {
    if (forceValidate || !touched) return undefined;
    const timer = setTimeout(() => setDebouncedInvalid(invalid), INPUT_VALIDATE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [invalid, touched, forceValidate]);
  const hasError = forceValidate ? invalid : debouncedInvalid;
  return (
    <Form.Item validateStatus={hasError ? 'error' : undefined} className="mb-0 [&_.rock-form-item-label]:hidden">
      <Tooltip title={hasError ? '此项必填' : undefined} placement="top">
        <div className="rounded-[6px] w-full">
          <Select
            size="small"
            value={needAiToSelectValue(row.need_ai)}
            placeholder="请选择"
            options={NEED_AI_OPTIONS}
            allowClear
            style={{ width: '100%' }}
            {...CELL_SELECT_DROPDOWN_PROPS}
            /* 2 选项下拉不加搜索框（覆盖须写在展开之后，否则被收口常量置回 true） */
            showSearch={false}
            onChange={(v: string) => {
              setTouched(true);
              onUpdate(row.id, { need_ai: needAiFromSelectValue(v) });
            }}
          />
        </div>
      </Tooltip>
    </Form.Item>
  );
});

export interface BuildNeedAiColumnParams {
  /** 列显隐：对齐折叠面板 `!view && detail?.structured && getCooperateAIRequired(category_id)` */
  showNeedAi: boolean;
  view: boolean;
  updateRow: (id: number, patch: Record<string, any>) => void;
  forceValidateAll: boolean;
  batchApplyRow: (field: string, bv: any, mode: 'keep' | 'cover') => void;
}

/** 构建「是否需包含AI」列；不适用（查看态 / 非结构化 / 非 AI 需求类型）时返回空数组 */
export function buildNeedAiColumn({
  showNeedAi,
  view,
  updateRow,
  forceValidateAll,
  batchApplyRow,
}: BuildNeedAiColumnParams): ColumnMeta<any, any>[] {
  if (!showNeedAi || view) return [];
  return [{
    kind: 'custom',
    custom: {
      key: 'need_ai',
      title: NEED_AI_TITLE,
      width: NEED_AI_COLUMN_WIDTH,
      render: (row: Record<string, any>) => (
        <NeedAiCell row={row} onUpdate={updateRow} forceValidate={forceValidateAll} />
      ),
      // 参与表格整体校验（引擎按列顺序遍历 custom 列的 validate，命中即滚动定位）
      validate: (row: Record<string, any>) =>
        isEmptyFieldValue(row.need_ai) ? `${NEED_AI_TITLE}必填` : undefined,
      batchEdit: {
        type: 'select',
        options: NEED_AI_OPTIONS,
        required: true,
        // 此处 2 选项，批量弹层下拉由引擎按选项数自动关闭搜索，无需额外声明
        apply: (value: string, mode: 'keep' | 'cover') =>
          batchApplyRow('need_ai', needAiFromSelectValue(value), mode),
      },
    },
  }];
}
